import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import swal from "sweetalert";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import Loading from "../../components/loading";
import ComboboxComponent from "../../components/Combobox";
import { getList } from "../../services/service";
import {
    createAkfaUnitMapping,
    deleteAkfaUnitMapping,
    getAkfaUnitMappings,
    updateAkfaUnitMapping,
} from "../../services/akfa/akfaService";
import { showError, showSuccess } from "./akfaUtils";

const EMPTY_FORM = { id: null, akfaUnitId: "", akfaUnitName: "", unitId: null };

const inputClass =
    "block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm";

// AKFA dilleri birliklari (Kg, sq.m, Штука...) ↔ WinPro c_unit moslashtiruvi — AKFA_UNIT_MAPPING_FRONTEND.md
export default function AkfaUnitMappingLayout() {
    const { t } = useTranslation();
    const { token } = useSelector((state) => state.user);

    const [items, setItems] = useState([]);
    const [units, setUnits] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [modal, setModal] = useState({ open: false, form: EMPTY_FORM });
    const [saving, setSaving] = useState(false);

    const fetchData = async () => {
        setIsLoading(true);
        const response = await getAkfaUnitMappings(token);
        if (response.success) {
            setItems(response.data || []);
        } else {
            showError(response, t("akfaIntegrationPage.common.load_error"));
        }
        setIsLoading(false);
    };

    useEffect(() => {
        fetchData();
        getList(`${process.env.REACT_APP_HOST_BASE_URL}/v1/unit/list`, token).then((res) => {
            if (res.success) setUnits(res.data || []);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const unitLabel = (unit) => (unit.shortName ? `${unit.name} (${unit.shortName})` : unit.name);
    const unitOptions = units.map((u) => ({ ...u, name: unitLabel(u) }));

    const openAdd = () => setModal({ open: true, form: EMPTY_FORM });

    const openEdit = (item) =>
        setModal({
            open: true,
            form: {
                id: item.id,
                akfaUnitId: String(item.akfaUnitId ?? ""),
                akfaUnitName: item.akfaUnitName || "",
                unitId: item.unitId ?? null,
            },
        });

    const closeModal = () => setModal({ open: false, form: EMPTY_FORM });

    const setField = (key, value) => setModal((s) => ({ ...s, form: { ...s.form, [key]: value } }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        const { id, akfaUnitId, akfaUnitName, unitId } = modal.form;
        const body = { akfaUnitId: Number(akfaUnitId), akfaUnitName: akfaUnitName.trim(), unitId };
        setSaving(true);
        const response = id ? await updateAkfaUnitMapping(token, id, body) : await createAkfaUnitMapping(token, body);
        setSaving(false);
        if (response.success) {
            showSuccess(id ? t("action.edit_success") : t("action.add_success"));
            closeModal();
            fetchData();
        } else {
            showError(response, t("akfaIntegrationPage.common.action_error"));
        }
    };

    const handleDelete = async (item) => {
        const confirmed = await swal({
            title: t("akfaUnitMappingPage.delete_confirm_title"),
            text: `${item.akfaUnitName} → ${item.unitName || "—"}`,
            icon: "warning",
            buttons: { cancel: true, confirm: true },
            dangerMode: true,
        });
        if (!confirmed) return;
        const response = await deleteAkfaUnitMapping(token, item.id);
        if (response.success) {
            showSuccess(t("akfaUnitMappingPage.delete_success"));
            fetchData();
        } else {
            showError(response, t("akfaIntegrationPage.common.action_error"));
        }
    };

    const unmappedCount = items.filter((item) => !item.unitId).length;

    return (
        <div className="px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                <h1 className="text-xl font-semibold text-gray-900">{t("akfaUnitMappingPage.title")}</h1>
                <button
                    onClick={openAdd}
                    className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-500"
                >
                    + {t("akfaUnitMappingPage.add")}
                </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">{t("akfaUnitMappingPage.description")}</p>

            {unmappedCount > 0 && (
                <div className="mb-4 rounded-md bg-yellow-50 border border-yellow-200 px-4 py-2 text-sm text-yellow-800">
                    ⚠ {t("akfaUnitMappingPage.unmapped_warning", { count: unmappedCount })}
                </div>
            )}

            {isLoading ? (
                <Loading />
            ) : (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-300">
                        <thead className="text-white bg-gray-700">
                            <tr>
                                <th className="py-3 pl-4 pr-3 text-left text-sm font-semibold">
                                    {t("akfaUnitMappingPage.col_akfa_id")}
                                </th>
                                <th className="py-3 pl-2 pr-3 text-left text-sm font-semibold">
                                    {t("akfaUnitMappingPage.col_akfa_name")}
                                </th>
                                <th className="py-3 pl-2 pr-3 text-left text-sm font-semibold">
                                    {t("akfaUnitMappingPage.col_winpro_unit")}
                                </th>
                                <th className="relative py-3 pl-3 pr-4">
                                    <span className="sr-only">{t("akfaIntegrationPage.common.actions")}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-300">
                            {items.length > 0 ? (
                                items.map((item) => (
                                    <tr key={item.id} className={item.unitId ? "hover:bg-gray-50" : "bg-yellow-50"}>
                                        <td className="pl-4 px-2 py-2 text-sm text-gray-700">{item.akfaUnitId}</td>
                                        <td className="px-2 py-2 text-sm font-medium text-gray-900">{item.akfaUnitName}</td>
                                        <td className="px-2 py-2 text-sm">
                                            {item.unitId ? (
                                                <span className="text-gray-700">
                                                    {item.unitName}
                                                    {item.unitShortName ? ` (${item.unitShortName})` : ""}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                    {t("akfaUnitMappingPage.not_selected")}
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-2 pr-4 text-right">
                                            <div className="flex justify-end gap-3">
                                                <button
                                                    onClick={() => openEdit(item)}
                                                    title={t("akfaIntegrationPage.mapping.edit_button")}
                                                    className="text-indigo-600 hover:text-indigo-800"
                                                >
                                                    <PencilSquareIcon className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(item)}
                                                    title={t("akfaUnitMappingPage.delete")}
                                                    className="text-red-600 hover:text-red-800"
                                                >
                                                    <TrashIcon className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={4} className="py-8 text-center text-sm text-gray-500">
                                        {t("akfaUnitMappingPage.empty")}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {modal.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            {modal.form.id ? t("akfaUnitMappingPage.edit_title") : t("akfaUnitMappingPage.add_title")}
                        </h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    {t("akfaUnitMappingPage.col_akfa_id")} *
                                </label>
                                <input
                                    type="number"
                                    required
                                    value={modal.form.akfaUnitId}
                                    onChange={(e) => setField("akfaUnitId", e.target.value)}
                                    className={inputClass}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    {t("akfaUnitMappingPage.col_akfa_name")} *
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={modal.form.akfaUnitName}
                                    onChange={(e) => setField("akfaUnitName", e.target.value)}
                                    className={inputClass}
                                />
                            </div>
                            <ComboboxComponent
                                label={t("akfaUnitMappingPage.col_winpro_unit")}
                                data={unitOptions}
                                item={modal.form.unitId ? unitOptions.find((u) => u.id === modal.form.unitId) || null : null}
                                onChange={(value) => setField("unitId", value?.id ?? null)}
                            />
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={closeModal}
                                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                {t("akfaIntegrationPage.common.cancel")}
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-500 disabled:opacity-60"
                            >
                                {saving ? t("akfaIntegrationPage.modal.saving") : t("akfaIntegrationPage.common.save")}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
